/**
 * Employee Management Page
 * Comprehensive church staff/worker management
 */

import React, { useState, useEffect } from 'react'
import { Plus, Search, Filter, MoreVertical, Users, DollarSign, UserPlus, Edit, Trash2, FileText, Wallet } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Textarea } from '@/components/ui/textarea'
import { AdminUser } from '@/lib/authService'
import { formatCurrency } from '@/lib/currency'
import EmployeeService, {
  Employee,
  EmploymentType,
  EmploymentStatus,
  CreateEmployeeInput,
  SalaryComponent,
  EmployeeSummary
} from '@/services/employeeService'
import { useSubscription } from '@/hooks/useSubscription'
import { FeatureRestricted } from '@/components/subscription/FeatureRestricted'

interface EmployeesProps {
  user: AdminUser | null
}

export default function Employees({ user }: EmployeesProps) {
  const { features, limits, loading: subscriptionLoading } = useSubscription(user?.churchId)
  const [employees, setEmployees] = useState<Employee[]>([])
  const [summary, setSummary] = useState<EmployeeSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('active')
  const [typeFilter, setTypeFilter] = useState<string>('all')
  const [showDialog, setShowDialog] = useState(false)
  const [showComponentsDialog, setShowComponentsDialog] = useState(false)
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null)
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null)
  const [salaryComponents, setSalaryComponents] = useState<SalaryComponent[]>([])
  const [saving, setSaving] = useState(false)

  // Form data for employee
  const [formData, setFormData] = useState<CreateEmployeeInput>({
    first_name: '',
    middle_name: '',
    last_name: '',
    date_of_birth: '',
    gender: '',
    marital_status: '',
    employment_type: 'full_time',
    department: '',
    position_title: '',
    job_grade: '',
    hire_date: new Date().toISOString().split('T')[0],
    tin: '',
    ssnit_number: '',
    national_id: '',
    email: '',
    phone: '',
    alternate_phone: '',
    address: '',
    city: '',
    region: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    emergency_contact_relationship: '',
    bank_name: '',
    bank_branch: '',
    account_number: '',
    account_name: '',
    basic_salary: 0,
    pay_frequency: 'monthly',
    currency: 'GHS',
    payment_method: 'bank_transfer',
    notes: ''
  })

  // Salary component form
  const [componentForm, setComponentForm] = useState({
    component_type: 'allowance' as 'allowance' | 'deduction',
    component_code: '',
    component_name: '',
    description: '',
    amount: '',
    calculation_type: 'fixed' as 'fixed' | 'percentage',
    percentage_basis: 'basic_salary',
    is_taxable: true,
    is_pensionable: false,
    is_active: true,
    start_date: new Date().toISOString().split('T')[0],
    end_date: ''
  })

  useEffect(() => {
    if (user?.churchId) {
      loadEmployees()
      loadSummary()
    }
  }, [user, statusFilter, typeFilter])

  async function loadEmployees() {
    if (!user?.churchId) return

    try {
      setLoading(true)
      const filters: any = {}

      if (statusFilter !== 'all') {
        filters.status = statusFilter as EmploymentStatus
      }

      if (typeFilter !== 'all') {
        filters.employment_type = typeFilter as EmploymentType
      }

      if (searchTerm) {
        filters.search = searchTerm
      }

      const data = await EmployeeService.getEmployees(user.churchId, filters)
      setEmployees(data)
    } catch (error) {
      console.error('Error loading employees:', error)
    } finally {
      setLoading(false)
    }
  }

  async function loadSummary() {
    if (!user?.churchId) return

    try {
      const data = await EmployeeService.getEmployeeSummary(user.churchId)
      setSummary(data)
    } catch (error) {
      console.error('Error loading summary:', error)
    }
  }

  function handleAddEmployee() {
    setEditingEmployee(null)
    setFormData({
      first_name: '',
      middle_name: '',
      last_name: '',
      date_of_birth: '',
      gender: '',
      marital_status: '',
      employment_type: 'full_time',
      department: '',
      position_title: '',
      job_grade: '',
      hire_date: new Date().toISOString().split('T')[0],
      tin: '',
      ssnit_number: '',
      national_id: '',
      email: '',
      phone: '',
      alternate_phone: '',
      address: '',
      city: '',
      region: '',
      emergency_contact_name: '',
      emergency_contact_phone: '',
      emergency_contact_relationship: '',
      bank_name: '',
      bank_branch: '',
      account_number: '',
      account_name: '',
      basic_salary: 0,
      pay_frequency: 'monthly',
      currency: 'GHS',
      payment_method: 'bank_transfer',
      notes: ''
    })
    setShowDialog(true)
  }

  function handleEditEmployee(employee: Employee) {
    setEditingEmployee(employee)
    setFormData({
      first_name: employee.first_name,
      middle_name: employee.middle_name || '',
      last_name: employee.last_name,
      date_of_birth: employee.date_of_birth || '',
      gender: employee.gender || '',
      marital_status: employee.marital_status || '',
      employment_type: employee.employment_type,
      department: employee.department || '',
      position_title: employee.position_title,
      job_grade: employee.job_grade || '',
      hire_date: employee.hire_date,
      tin: employee.tin || '',
      ssnit_number: employee.ssnit_number || '',
      national_id: employee.national_id || '',
      email: employee.email || '',
      phone: employee.phone || '',
      alternate_phone: '',
      address: employee.address || '',
      city: '',
      region: '',
      emergency_contact_name: employee.emergency_contact_name || '',
      emergency_contact_phone: employee.emergency_contact_phone || '',
      emergency_contact_relationship: '',
      bank_name: employee.bank_name || '',
      bank_branch: '',
      account_number: employee.account_number || '',
      account_name: employee.account_name || '',
      basic_salary: employee.basic_salary,
      pay_frequency: employee.pay_frequency,
      currency: employee.currency,
      payment_method: 'bank_transfer',
      notes: ''
    })
    setShowDialog(true)
  }

  async function handleSaveEmployee() {
    if (!user?.churchId || !user.id) return

    // Validation
    if (!formData.first_name.trim() || !formData.last_name.trim()) {
      alert('Error: Please enter employee name')
      return
    }
    if (!formData.position_title.trim()) {
      alert('Error: Please enter position title')
      return
    }
    if (formData.basic_salary <= 0) {
      alert('Error: Please enter valid basic salary')
      return
    }

    setSaving(true)
    try {
      if (editingEmployee) {
        await EmployeeService.updateEmployee(editingEmployee.id, formData, user.id)
        alert('Success: Employee updated successfully')
      } else {
        await EmployeeService.createEmployee(user.churchId, formData, user.id)
        alert('Success: Employee created successfully')
      }

      setShowDialog(false)
      loadEmployees()
      loadSummary()
    } catch (error) {
      console.error('Error saving employee:', error)
      alert(`Error: ${error instanceof Error ? error.message : 'Failed to save employee'}`)
    } finally {
      setSaving(false)
    }
  }

  async function handleManageComponents(employee: Employee) {
    setSelectedEmployee(employee)
    try {
      const components = await EmployeeService.getSalaryComponents(employee.id)
      setSalaryComponents(components)
      setShowComponentsDialog(true)
    } catch (error) {
      console.error('Error loading salary components:', error)
    }
  }

  async function handleAddComponent() {
    if (!user?.churchId || !selectedEmployee || !user.id) return

    if (!componentForm.component_name.trim() || !componentForm.component_code.trim()) {
      alert('Error: Please enter component name and code')
      return
    }

    if (parseFloat(componentForm.amount) <= 0) {
      alert('Error: Please enter valid amount')
      return
    }

    try {
      await EmployeeService.addSalaryComponent(
        user.churchId,
        {
          employee_id: selectedEmployee.id,
          ...componentForm,
          amount: parseFloat(componentForm.amount)
        },
        user.id
      )

      alert('Success: Component added')

      // Refresh components
      const components = await EmployeeService.getSalaryComponents(selectedEmployee.id)
      setSalaryComponents(components)

      // Reset form
      setComponentForm({
        component_type: 'allowance',
        component_code: '',
        component_name: '',
        description: '',
        amount: '',
        calculation_type: 'fixed',
        percentage_basis: 'basic_salary',
        is_taxable: true,
        is_pensionable: false,
        is_active: true,
        start_date: new Date().toISOString().split('T')[0],
        end_date: ''
      })
    } catch (error) {
      console.error('Error adding component:', error)
      alert(`Error: ${error instanceof Error ? error.message : 'Failed to add component'}`)
    }
  }

  async function handleDeleteComponent(componentId: string) {
    if (!confirm('Are you sure you want to delete this component?')) return

    try {
      await EmployeeService.deleteSalaryComponent(componentId)
      alert('Success: Component deleted')

      // Refresh components
      if (selectedEmployee) {
        const components = await EmployeeService.getSalaryComponents(selectedEmployee.id)
        setSalaryComponents(components)
      }
    } catch (error) {
      console.error('Error deleting component:', error)
      alert(`Error: ${error instanceof Error ? error.message : 'Failed to delete component'}`)
    }
  }

  const filteredEmployees = employees.filter(emp =>
    searchTerm === '' ||
    emp.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.employee_code.toLowerCase().includes(searchTerm.toLowerCase())
  )

  if (loading || subscriptionLoading) {
    return (
      <div className="flex justify-center items-center h-full">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
      </div>
    )
  }

  if (!features.hrPayroll) {
    return (
      <FeatureRestricted
        featureName="Employee Management"
        description="Manage church staff and employees with complete HR records, leave tracking, and performance reviews."
        currentPlan={limits?.plan || 'starter'}
        requiredPlan="leadership"
        icon={<Users className="h-10 w-10 text-white" />}
      />
    )
  }

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="border-b bg-background px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Employee Management</h1>
            <p className="text-sm text-muted-foreground">
              Manage church staff and workers
            </p>
          </div>
          <Button onClick={handleAddEmployee}>
            <Plus className="mr-2 h-4 w-4" />
            Add Employee
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto p-6">
        {/* Summary Cards */}
        <div className="mb-6 grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Employees</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{summary?.total_employees || 0}</div>
              <p className="text-xs text-muted-foreground mt-1">
                {summary?.active_employees || 0} active
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Full-Time</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{summary?.full_time || 0}</div>
              <p className="text-xs text-muted-foreground mt-1">
                {summary?.part_time || 0} part-time
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Contract</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{summary?.contract || 0}</div>
              <p className="text-xs text-muted-foreground mt-1">
                contractors
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Monthly Cost</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {formatCurrency(summary?.total_monthly_cost || 0, 'GHS')}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Basic salaries
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search employees..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          <div className="flex gap-2">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]">
                <Filter className="mr-2 h-4 w-4" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="on_leave">On Leave</SelectItem>
                <SelectItem value="suspended">Suspended</SelectItem>
                <SelectItem value="terminated">Terminated</SelectItem>
              </SelectContent>
            </Select>

            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="full_time">Full-Time</SelectItem>
                <SelectItem value="part_time">Part-Time</SelectItem>
                <SelectItem value="contract">Contract</SelectItem>
                <SelectItem value="casual">Casual</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Employees Table */}
        <Card>
          <CardHeader>
            <CardTitle>Employees</CardTitle>
            <CardDescription>
              {filteredEmployees.length} employees found
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee Code</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Position</TableHead>
                    <TableHead>Department</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">Basic Salary</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredEmployees.map((employee) => (
                    <TableRow key={employee.id}>
                      <TableCell className="font-medium">{employee.employee_code}</TableCell>
                      <TableCell>
                        <div>
                          <div className="font-medium">
                            {employee.first_name} {employee.last_name}
                          </div>
                          {employee.email && (
                            <div className="text-xs text-muted-foreground">
                              {employee.email}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{employee.position_title}</TableCell>
                      <TableCell>{employee.department || '-'}</TableCell>
                      <TableCell>
                        <span className="capitalize">{employee.employment_type.replace('_', ' ')}</span>
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(employee.basic_salary, employee.currency)}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            employee.employment_status === 'active'
                              ? 'default'
                              : employee.employment_status === 'terminated'
                              ? 'secondary'
                              : 'destructive'
                          }
                        >
                          {employee.employment_status.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleEditEmployee(employee)}>
                              <Edit className="mr-2 h-4 w-4" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleManageComponents(employee)}>
                              <DollarSign className="mr-2 h-4 w-4" />
                              Salary Components
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
          </CardContent>
        </Card>
      </div>

      {/* Add/Edit Employee Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingEmployee ? 'Edit Employee' : 'Add New Employee'}</DialogTitle>
            <DialogDescription>
              {editingEmployee ? 'Update employee information' : 'Create a new employee record'}
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="personal" className="w-full">
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="personal">Personal</TabsTrigger>
              <TabsTrigger value="employment">Employment</TabsTrigger>
              <TabsTrigger value="statutory">Statutory</TabsTrigger>
              <TabsTrigger value="banking">Banking</TabsTrigger>
              <TabsTrigger value="emergency">Emergency</TabsTrigger>
            </TabsList>

            {/* Personal Information */}
            <TabsContent value="personal" className="space-y-4">
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="first_name">First Name *</Label>
                  <Input
                    id="first_name"
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    placeholder="Enter first name"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="middle_name">Middle Name</Label>
                  <Input
                    id="middle_name"
                    value={formData.middle_name}
                    onChange={(e) => setFormData({ ...formData, middle_name: e.target.value })}
                    placeholder="Enter middle name"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="last_name">Last Name *</Label>
                  <Input
                    id="last_name"
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    placeholder="Enter last name"
                  />
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="date_of_birth">Date of Birth</Label>
                  <Input
                    id="date_of_birth"
                    type="date"
                    value={formData.date_of_birth}
                    onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="gender">Gender</Label>
                  <Select
                    value={formData.gender}
                    onValueChange={(value) => setFormData({ ...formData, gender: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select gender" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="marital_status">Marital Status</Label>
                  <Select
                    value={formData.marital_status}
                    onValueChange={(value) => setFormData({ ...formData, marital_status: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="single">Single</SelectItem>
                      <SelectItem value="married">Married</SelectItem>
                      <SelectItem value="divorced">Divorced</SelectItem>
                      <SelectItem value="widowed">Widowed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="employee@example.com"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+233 XX XXX XXXX"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="address">Address</Label>
                <Textarea
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Enter residential address"
                  rows={2}
                />
              </div>
            </TabsContent>

            {/* Employment Information */}
            <TabsContent value="employment" className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="position_title">Position Title *</Label>
                  <Input
                    id="position_title"
                    value={formData.position_title}
                    onChange={(e) => setFormData({ ...formData, position_title: e.target.value })}
                    placeholder="e.g., Senior Pastor"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="department">Department</Label>
                  <Input
                    id="department"
                    value={formData.department}
                    onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                    placeholder="e.g., Ministry"
                  />
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="employment_type">Employment Type *</Label>
                  <Select
                    value={formData.employment_type}
                    onValueChange={(value: any) => setFormData({ ...formData, employment_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="full_time">Full-Time</SelectItem>
                      <SelectItem value="part_time">Part-Time</SelectItem>
                      <SelectItem value="contract">Contract</SelectItem>
                      <SelectItem value="casual">Casual</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="hire_date">Hire Date *</Label>
                  <Input
                    id="hire_date"
                    type="date"
                    value={formData.hire_date}
                    onChange={(e) => setFormData({ ...formData, hire_date: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="basic_salary">Basic Salary (GHS) *</Label>
                  <Input
                    id="basic_salary"
                    type="number"
                    step="0.01"
                    value={formData.basic_salary}
                    onChange={(e) => setFormData({ ...formData, basic_salary: parseFloat(e.target.value) || 0 })}
                    placeholder="0.00"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="pay_frequency">Pay Frequency</Label>
                  <Select
                    value={formData.pay_frequency}
                    onValueChange={(value: any) => setFormData({ ...formData, pay_frequency: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="daily">Daily</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="job_grade">Job Grade</Label>
                  <Input
                    id="job_grade"
                    value={formData.job_grade}
                    onChange={(e) => setFormData({ ...formData, job_grade: e.target.value })}
                    placeholder="e.g., Grade 5"
                  />
                </div>
              </div>
            </TabsContent>

            {/* Statutory Information */}
            <TabsContent value="statutory" className="space-y-4">
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="tin">TIN (Tax ID Number)</Label>
                  <Input
                    id="tin"
                    value={formData.tin}
                    onChange={(e) => setFormData({ ...formData, tin: e.target.value })}
                    placeholder="C0012345678"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="ssnit_number">SSNIT Number</Label>
                  <Input
                    id="ssnit_number"
                    value={formData.ssnit_number}
                    onChange={(e) => setFormData({ ...formData, ssnit_number: e.target.value })}
                    placeholder="C123456789012"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="national_id">National ID / Passport</Label>
                  <Input
                    id="national_id"
                    value={formData.national_id}
                    onChange={(e) => setFormData({ ...formData, national_id: e.target.value })}
                    placeholder="GHA-XXXXXXXXX-X"
                  />
                </div>
              </div>
            </TabsContent>

            {/* Banking Information */}
            <TabsContent value="banking" className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="bank_name">Bank Name</Label>
                  <Input
                    id="bank_name"
                    value={formData.bank_name}
                    onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                    placeholder="e.g., GCB Bank"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="bank_branch">Branch</Label>
                  <Input
                    id="bank_branch"
                    value={formData.bank_branch}
                    onChange={(e) => setFormData({ ...formData, bank_branch: e.target.value })}
                    placeholder="e.g., Accra Main"
                  />
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="account_number">Account Number</Label>
                  <Input
                    id="account_number"
                    value={formData.account_number}
                    onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
                    placeholder="1234567890"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="account_name">Account Name</Label>
                  <Input
                    id="account_name"
                    value={formData.account_name}
                    onChange={(e) => setFormData({ ...formData, account_name: e.target.value })}
                    placeholder="Full name as on account"
                  />
                </div>
              </div>
            </TabsContent>

            {/* Emergency Contact */}
            <TabsContent value="emergency" className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="emergency_contact_name">Contact Name</Label>
                  <Input
                    id="emergency_contact_name"
                    value={formData.emergency_contact_name}
                    onChange={(e) => setFormData({ ...formData, emergency_contact_name: e.target.value })}
                    placeholder="Full name"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="emergency_contact_phone">Contact Phone</Label>
                  <Input
                    id="emergency_contact_phone"
                    value={formData.emergency_contact_phone}
                    onChange={(e) => setFormData({ ...formData, emergency_contact_phone: e.target.value })}
                    placeholder="+233 XX XXX XXXX"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="emergency_contact_relationship">Relationship</Label>
                <Input
                  id="emergency_contact_relationship"
                  value={formData.emergency_contact_relationship}
                  onChange={(e) => setFormData({ ...formData, emergency_contact_relationship: e.target.value })}
                  placeholder="e.g., Spouse, Parent, Sibling"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Additional Notes</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Any additional information"
                  rows={4}
                />
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSaveEmployee} disabled={saving}>
              {saving ? 'Saving...' : editingEmployee ? 'Update Employee' : 'Create Employee'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Salary Components Dialog */}
      <Dialog open={showComponentsDialog} onOpenChange={setShowComponentsDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Salary Components - {selectedEmployee?.first_name} {selectedEmployee?.last_name}</DialogTitle>
            <DialogDescription>
              Manage allowances and deductions for this employee
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Add New Component */}
            <Card>
              <CardHeader>
                <CardTitle>Add Component</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Type</Label>
                    <Select
                      value={componentForm.component_type}
                      onValueChange={(value: any) => setComponentForm({ ...componentForm, component_type: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="allowance">Allowance</SelectItem>
                        <SelectItem value="deduction">Deduction</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Code</Label>
                    <Input
                      value={componentForm.component_code}
                      onChange={(e) => setComponentForm({ ...componentForm, component_code: e.target.value })}
                      placeholder="e.g., housing_allowance"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Name</Label>
                  <Input
                    value={componentForm.component_name}
                    onChange={(e) => setComponentForm({ ...componentForm, component_name: e.target.value })}
                    placeholder="e.g., Housing Allowance"
                  />
                </div>

                <div className="grid gap-4 md:grid-cols-3">
                  <div className="space-y-2">
                    <Label>Calculation Type</Label>
                    <Select
                      value={componentForm.calculation_type}
                      onValueChange={(value: any) => setComponentForm({ ...componentForm, calculation_type: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fixed">Fixed Amount</SelectItem>
                        <SelectItem value="percentage">Percentage</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Amount {componentForm.calculation_type === 'percentage' ? '(%)' : '(GHS)'}</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={componentForm.amount}
                      onChange={(e) => setComponentForm({ ...componentForm, amount: e.target.value })}
                      placeholder="0.00"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Taxable</Label>
                    <Select
                      value={componentForm.is_taxable ? 'yes' : 'no'}
                      onValueChange={(value) => setComponentForm({ ...componentForm, is_taxable: value === 'yes' })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="yes">Yes</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <Button onClick={handleAddComponent}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Component
                </Button>
              </CardContent>
            </Card>

            {/* Existing Components */}
            <div className="space-y-4">
              <h3 className="font-semibold">Current Components</h3>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Taxable</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {salaryComponents.map((comp) => (
                    <TableRow key={comp.id}>
                      <TableCell>
                        <Badge variant={comp.component_type === 'allowance' ? 'default' : 'secondary'}>
                          {comp.component_type}
                        </Badge>
                      </TableCell>
                      <TableCell>{comp.component_name}</TableCell>
                      <TableCell>
                        {comp.calculation_type === 'percentage'
                          ? `${comp.amount}%`
                          : formatCurrency(comp.amount, 'GHS')}
                      </TableCell>
                      <TableCell>{comp.is_taxable ? 'Yes' : 'No'}</TableCell>
                      <TableCell>
                        <Badge variant={comp.is_active ? 'default' : 'secondary'}>
                          {comp.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => comp.id && handleDeleteComponent(comp.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {salaryComponents.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-muted-foreground">
                        No components added yet
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>

          <DialogFooter>
            <Button onClick={() => setShowComponentsDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
